import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import useOnboardingContext from "@/hooks/useOnboardingContext";
import useGridSitewizardPipeline from "@/hooks/useGridSitewizardPipeline";
import { useTemplateContext } from "@/context/TemplateProvider";
import { uploadImageToImageLibrary } from "@/api/onboarding";
import { STEPS } from "@/constants/onboarding";
import isEmpty from 'lodash/isEmpty'
import useIsMobile from '../../../../hooks/useIsMobile'

const WizardFooter = ({ skip, disabled = false, customText, handleExitToEditor }) => {
  const navigate = useNavigate();
  const { actualStep, setActualStep, actualSubStep, setActualSubStep, setStepSequence, isGrid, siteplusLoginUrl, sections, setExit, storeOnboardingData: { appChoices } } = useOnboardingContext();
  const { template: { sections: { hero }, logo } } = useTemplateContext();
  const { runGridPipelineAfterLeaveStyle } = useGridSitewizardPipeline();

  // Check if appointments or store was checked on step 8
  const hasAppointmentsOrStore = sections?.appointments || sections?.store;
  
  // Check if appointments or store was previously selected in store onboarding
  const previousAppChoices = JSON.parse(appChoices ?? "{}");
  const hadPreviousAppointmentsOrStore = previousAppChoices?.appointments || previousAppChoices?.store;

  const step = STEPS[actualStep];
  const currentStep = step.subSteps ? step.subSteps[actualSubStep] : step;
  const isFullScreenStep = currentStep.isFullScreen;
  const { wizard } = currentStep;
  
  // Handle dynamic button text - check if button is a function
  const getButtonText = () => {
    if (skip) return wizard.skipTo;
    if (typeof wizard.button === 'function') {
      return wizard.button(isGrid);
    }
    if (hasAppointmentsOrStore && currentStep.key === 'sections' && !hadPreviousAppointmentsOrStore) {
      return wizard.buttonReview;
    }
    if (isGrid && wizard.buttonGrid) {
      return wizard.buttonGrid;
    }
    return wizard.button;
  };
  
  const nextButtonText = getButtonText();
  const { isMobile } = useIsMobile()

  // Generic helper to check if a step should be skipped
  const shouldSkipStep = (stepIndex, subStepIndex) => {
    const stepToCheck = STEPS[stepIndex];
    const subStepToCheck = stepToCheck?.subSteps?.[subStepIndex];
    const step = subStepToCheck || stepToCheck;
    return step?.skipWhen?.(isGrid) === true;
  };

  const findNextValidStep = (startStepIndex, startSubStepIndex) => {
    let currentStepIndex = startStepIndex;
    let currentSubStepIndex = startSubStepIndex;
    const visited = new Set();

    while (true) {
      const key = `${currentStepIndex}-${currentSubStepIndex}`;
      if (visited.has(key)) break; // Prevent infinite loops
      visited.add(key);

      const currentStep = STEPS[currentStepIndex];
      const hasSubSteps = currentStep?.subSteps?.length > 0;

      if (hasSubSteps) {
        if (currentSubStepIndex < currentStep.subSteps.length - 1) {
          currentSubStepIndex++;
        } else {
          currentStepIndex++;
          currentSubStepIndex = 0;
        }
      } else {
        currentStepIndex++;
        currentSubStepIndex = 0;
      }

      if (currentStepIndex >= STEPS.length) break;
      if (!shouldSkipStep(currentStepIndex, currentSubStepIndex)) {
        return { stepIndex: currentStepIndex, subStepIndex: currentSubStepIndex };
      }
    }

    return { stepIndex: startStepIndex, subStepIndex: startSubStepIndex };
  };

  const findPreviousValidStep = (startStepIndex, startSubStepIndex) => {
    let currentStepIndex = startStepIndex;
    let currentSubStepIndex = startSubStepIndex;
    const visited = new Set();

    while (true) {
      const key = `${currentStepIndex}-${currentSubStepIndex}`;
      if (visited.has(key)) break; // Prevent infinite loops
      visited.add(key);

      const currentStep = STEPS[currentStepIndex];
      const hasSubSteps = currentStep?.subSteps?.length > 0;

      if (hasSubSteps) {
        if (currentSubStepIndex > 0) {
          currentSubStepIndex--;
        } else {
          currentStepIndex--;
          if (currentStepIndex < 0) break;
          const previousStep = STEPS[currentStepIndex];
          currentSubStepIndex = previousStep?.subSteps?.length - 1 || 0;
        }
      } else {
        currentStepIndex--;
        currentSubStepIndex = 0;
      }

      if (currentStepIndex < 0) break;
      if (!shouldSkipStep(currentStepIndex, currentSubStepIndex)) {
        return { stepIndex: currentStepIndex, subStepIndex: currentSubStepIndex };
      }
    }

    return { stepIndex: startStepIndex, subStepIndex: startSubStepIndex };
  };

  const handleStepper = (isBack = false) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    if (isBack) {
      if (actualStep === 0 && actualSubStep === 0) {
        navigate("/", { replace: true });
        setActualStep(0);
        setActualSubStep(0);
        setStepSequence(null);
        return;
      }

      // Handle skipping the "loader" step when going back
      if (actualStep > 0 && STEPS[actualStep - 1]?.key === "loader") {
        setActualStep((prev) => prev - 2);
        setActualSubStep(0);
        return;
      }

      const { stepIndex, subStepIndex } = findPreviousValidStep(actualStep, actualSubStep);
      setActualStep(stepIndex);
      setActualSubStep(subStepIndex);
    } else {
      // When on sections step, always set exit flag so loader redirects to editor
      if (actualStep == 2 && actualSubStep == 3 && isGrid) {
        setExit(true)
        // Skip apps-setup and go directly to loader if no store/appointments selected (current or previous)
        if (!hasAppointmentsOrStore || hadPreviousAppointmentsOrStore) {
          handleExitToEditor()
          return
        }
      }
      if(skip && wizard.skippable) {
        const stepIndex = STEPS.findIndex(x => x.title === wizard.skipStepGroup)
        if(stepIndex > -1) setActualStep(stepIndex)
        if(!isEmpty(STEPS[stepIndex].subSteps)) {
          const subsStepIndex = STEPS[stepIndex].subSteps.findIndex(x => x.key === wizard.skipTo.toLowerCase())
          if(subsStepIndex > -1) setActualSubStep(subsStepIndex)
        }
        return
      }

      if (isGrid && actualStep === 0 && actualSubStep === 0) {
        runGridPipelineAfterLeaveStyle();
      }

      const { stepIndex, subStepIndex } = findNextValidStep(actualStep, actualSubStep);
      setActualStep(stepIndex);
      setActualSubStep(subStepIndex);
    }
  };

  const onGoBack = () => {
    handleStepper(true);
  };

  const handleCreateWebsite = async () => {
    const imagesToUpload = [
      hero.backgroundImage && { image: hero.backgroundImage, tag: "banner" },
      logo.src && { image: logo.src, tag: "logo" },
    ].filter(Boolean);
    
    if (imagesToUpload.length > 0) {
      try {
        await uploadImageToImageLibrary(imagesToUpload);
      } catch (error) {
        console.error("[WizardFooter] uploadImageToImageLibrary failed:", error);
      }
    }
    handleStepper();
  };

  return (
    <Box
      bgcolor={
        isMobile
          ? 'rgba(255,255,255,.9)'
          : isFullScreenStep
          ? ''
          : 'rgba(255,255,255,.9)'
      }
      className='backdrop-blur-sm fixed left-0 right-5 md:right-0 md:sticky bottom-0 flex justify-end gap-2 pt-2 pb-2 z-[100]'
      sx={{
        '@media (max-width: 767px)': {
          borderTop: '1px solid rgba(0,0,0,.1)',
          margin: '0',
          width: '100%',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
        },
      }}
    >
      <Button
        className='!font-medium'
        variant='text'
        sx={{ color: '#656B77' }}
        onClick={onGoBack}
      >
        Back
      </Button>
      {currentStep.key === 'sections' && (!hasAppointmentsOrStore || hadPreviousAppointmentsOrStore) && isGrid ? (
        <Button
          variant='contained'
          sx={{
            padding: '8px 24px 10px',
            fontSize: '16px',
          }}
          onClick={handleCreateWebsite}
          disabled={disabled}
        >
          {nextButtonText}
        </Button>
      ) : (
        <Button
          variant='contained'
          sx={{
            padding: '8px 24px 10px',
            fontSize: '16px',
            '& .MuiTypography-root': { fontSize: 'inherit' },
          }}
          onClick={() => {
            handleStepper()
          }}
          disabled={disabled}
        >
          {!isEmpty(customText) ? customText.text : skip ? 'Skip' : 'Next'}
          {'\u00A0\u2014\u00A0'}
          <Typography component='span' className='max-lg:hidden' fontStyle='italic' sx={{ fontSize: 'inherit' }}>
            {!isEmpty(customText) ? customText.next : nextButtonText}
          </Typography>
        </Button>
      )}
    </Box>
  )
};

WizardFooter.propTypes = {
  skip: PropTypes.bool,
  disabled: PropTypes.bool,
  customText: PropTypes.object,
  handleExitToEditor: PropTypes.func
}
export default WizardFooter;
